import { supabase } from './supabase'

export interface RelevanceSettings {
  scoreThreshold: number
  prompt: string
  minDescriptionLength: number
  maxDescriptionLength: number
}

export const DEFAULT_RELEVANCE_PROMPT = 'Analyze this job posting. Return JSON: {"score":0-100,"verdict":"Fit"/"No Fit","reasoning":"..."}.\n\n'

const DEFAULT_RELEVANCE_SETTINGS: RelevanceSettings = {
  scoreThreshold: 75,
  prompt: DEFAULT_RELEVANCE_PROMPT,
  minDescriptionLength: 50,
  maxDescriptionLength: 3000,
}

/**
 * Loads relevance analysis settings from user_settings table
 * Returns default values if settings don't exist or user is not authenticated
 */
export async function loadRelevanceSettings(): Promise<RelevanceSettings> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      return DEFAULT_RELEVANCE_SETTINGS
    }

    // Try to load settings, but handle errors gracefully
    const { data, error } = await supabase
      .from('user_settings')
      .select('relevance_score_threshold, relevance_prompt, relevance_min_description_length, relevance_max_description_length')
      .eq('user_id', user.id)
      .maybeSingle()

    if (error) {
      // If columns don't exist (code 42703) or any other error, silently use defaults
      // This happens when migration hasn't been applied yet or columns don't exist
      // Don't log to console to avoid noise - this is expected behavior
      return DEFAULT_RELEVANCE_SETTINGS
    }

    if (!data) {
      return DEFAULT_RELEVANCE_SETTINGS
    }

    return {
      scoreThreshold: data.relevance_score_threshold ?? DEFAULT_RELEVANCE_SETTINGS.scoreThreshold,
      prompt: data.relevance_prompt ?? DEFAULT_RELEVANCE_SETTINGS.prompt,
      minDescriptionLength: data.relevance_min_description_length ?? DEFAULT_RELEVANCE_SETTINGS.minDescriptionLength,
      maxDescriptionLength: data.relevance_max_description_length ?? DEFAULT_RELEVANCE_SETTINGS.maxDescriptionLength,
    }
  } catch (err) {
    // Silently use defaults on any error - don't log to console
    // This prevents console noise when columns don't exist
    return DEFAULT_RELEVANCE_SETTINGS
  }
}

/**
 * Formats the prompt template by replacing placeholders with actual values
 */
export function formatRelevancePrompt(
  promptTemplate: string,
  company: string,
  title: string,
  location: string,
  description: string
): string {
  return promptTemplate
    .replace(/{company}/g, company || 'Unknown')
    .replace(/{title}/g, title || 'Unknown')
    .replace(/{location}/g, location || 'Unknown')
    .replace(/{description}/g, description)
}
